// src/utils/services/openaiClient.js
// Cliente generico para llamar a OpenAI a traves del proxy del backend.
//
// La API Key de OpenAI vive solo en el servidor (AIProxyController la inyecta).
// El front solo manda el JWT del usuario en Authorization.
//
// Endpoints proxy (backend):
//   POST  /api/AI/chat/completions   (JSON)
//   POST  /api/AI/files              (multipart/form-data, max 50 MB)
//   POST  /api/AI/responses          (JSON)
//
// Modelos permitidos por el proxy (whitelist en backend):
//   gpt-4o-mini, gpt-4o, gpt-4, gpt-4.1, gpt-4.1-mini, gpt-3.5-turbo
//
// Documentacion del proxy: hipaa-logs/Docs/Control/15_ENDPOINTS_AI_PROXY.md
//                          hipaa-logs/Docs/Control/16_FRONTEND_AI_PROXY_MIGRATION.md

import API_BASE_URL from '../../config/apiConfig';

const PROXY_BASE = `${API_BASE_URL}/api/AI`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

/**
 * Llama a /api/AI/chat/completions (proxy del backend a OpenAI).
 * El body es identico al de OpenAI; el backend lo reenvia tal cual.
 * La respuesta tambien se devuelve identica (shape de OpenAI).
 *
 * @param {Object} params
 * @param {string} params.model - Modelo (ej: "gpt-4o-mini")
 * @param {Array}  params.messages - [{role, content}]
 * @param {number} [params.temperature]
 * @param {number} [params.max_tokens]
 */
export const callOpenAIChat = async ({
  model = 'gpt-4o-mini',
  messages,
  temperature = 0.3,
  max_tokens,
}) => {
  const body = { model, messages, temperature };
  if (max_tokens) body.max_tokens = max_tokens;

  const response = await fetch(`${PROXY_BASE}/chat/completions`, {
    method: 'POST',
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[callOpenAIChat] Error HTTP:', response.status, errorText);
    throw new Error(`AI Proxy error: ${response.status}`);
  }

  return response.json();
};

/**
 * Sube un archivo a /api/AI/files (proxy a OpenAI Files API).
 * Limite del proxy: 50 MB por archivo.
 *
 * @param {File} file - Archivo a subir (ej: PDF clinico)
 * @returns {Promise<{id: string, object: string, bytes: number, ...}>}
 */
export const uploadFileToOpenAI = async (file) => {
  if (!file) throw new Error('No file provided');

  const form = new FormData();
  form.append('file', file);
  form.append('purpose', 'assistants');

  const response = await fetch(`${PROXY_BASE}/files`, {
    method: 'POST',
    headers: {
      // NO setear Content-Type: el browser lo arma con el boundary correcto
      ...getAuthHeader(),
    },
    body: form,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[uploadFileToOpenAI] Error HTTP:', response.status, errorText);
    throw new Error(`AI Proxy upload error: ${response.status}`);
  }

  return response.json();
};

/**
 * Llama a /api/AI/responses (proxy a Responses API de OpenAI).
 * Usado para analizar PDFs despues de subirlos con uploadFileToOpenAI.
 *
 * @param {Object} params
 * @param {string} params.model
 * @param {Array}  params.input - Estructura input de la Responses API
 */
export const callOpenAIResponses = async ({ model = 'gpt-4o-mini', input }) => {
  const response = await fetch(`${PROXY_BASE}/responses`, {
    method: 'POST',
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ model, input }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[callOpenAIResponses] Error HTTP:', response.status, errorText);
    throw new Error(`AI Proxy responses error: ${response.status}`);
  }

  return response.json();
};
